import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import type { CalendarEvent } from "../types/calendarEventTypes";
import { createAgenda, eventDaySegment } from "./agenda";
import { eventLocalValue } from "./eventTime";

export const TIMELINE_PX_PER_MINUTE = 1.6;
const SPOT_MINUTES = 15;
const CLOSED_MARGIN_MINUTES = 30;
const DEFAULT_APPOINTMENT_MINUTES = 30;

type Range = { startAt: Date; endAt: Date };
type Span = { startMinute: number; endMinute: number };
type TimelineItem = Span & Range & (
  | { kind: "appointment"; id: string; appointment: Appointment }
  | { kind: "event"; id: string; event: CalendarEvent; continuesBefore: boolean; continuesAfter: boolean }
);

export type TimelineBlock = TimelineItem & { lane: number; lanes: number };

export interface DayTimeline {
  allDay: CalendarEvent[];
  /** Null when the day is closed and has nothing timed on it. */
  range: { startMinute: number; endMinute: number } | null;
  hours: number[];
  open: { startMinute: number; endMinute: number }[];
  closed: { startMinute: number; endMinute: number }[];
  blocks: TimelineBlock[];
  spots: (Range & { startMinute: number; endMinute: number })[];
}

/** Wall-clock minute in the business timezone, clamped to the day so DST days keep their labels. */
export function minuteOfDay(date: Date, dateKey: string, timezone: string) {
  const local = eventLocalValue(date, timezone);
  const key = local.slice(0, 10);
  if (key < dateKey) return 0;
  if (key > dateKey) return 1440;
  return Number(local.slice(11, 13)) * 60 + Number(local.slice(14, 16));
}

export function timelineOffset(minute: number, startMinute: number) {
  return (minute - startMinute) * TIMELINE_PX_PER_MINUTE;
}

function assignLanes<T extends { startMinute: number; endMinute: number }>(items: T[]): (T & { lane: number; lanes: number })[] {
  const placed: (T & { lane: number; lanes: number })[] = [];
  let cluster: (T & { lane: number; lanes: number })[] = [];
  let clusterEnd = -1;
  const closeCluster = () => {
    const lanes = Math.max(1, ...cluster.map((item) => item.lane + 1));
    for (const item of cluster) item.lanes = lanes;
    cluster = [];
  };
  for (const item of [...items].sort((a, b) => a.startMinute - b.startMinute || b.endMinute - a.endMinute)) {
    if (item.startMinute >= clusterEnd) closeCluster();
    const laneEnds = cluster.reduce<number[]>((ends, other) => {
      ends[other.lane] = Math.max(ends[other.lane] ?? -1, other.endMinute);
      return ends;
    }, []);
    let lane = laneEnds.findIndex((end) => end <= item.startMinute);
    if (lane === -1) lane = laneEnds.length;
    const next = { ...item, lane, lanes: 1 };
    cluster.push(next);
    placed.push(next);
    clusterEnd = Math.max(clusterEnd, item.endMinute);
  }
  closeCluster();
  return placed;
}

export function createDayTimeline(params: {
  appointments: Appointment[];
  events: CalendarEvent[];
  dateKey: string;
  timezone: string;
  openWindows: Range[];
  gaps: Range[];
}): DayTimeline {
  const { dateKey, timezone } = params;
  const minute = (date: Date) => minuteOfDay(date, dateKey, timezone);
  const agenda = createAgenda(params.appointments, params.events, dateKey, timezone);

  const blocks: TimelineBlock[] = assignLanes(agenda.timed.flatMap((item): TimelineItem[] => {
    if (item.kind === "appointment" && item.appointment.startAt) {
      const startAt = item.appointment.startAt;
      const endAt = item.appointment.endAt && item.appointment.endAt > startAt
        ? item.appointment.endAt : new Date(startAt.getTime() + DEFAULT_APPOINTMENT_MINUTES * 60_000);
      return [{ kind: "appointment" as const, id: item.id, appointment: item.appointment, startAt, endAt,
        startMinute: minute(startAt), endMinute: Math.max(minute(endAt), minute(startAt) + SPOT_MINUTES) }];
    }
    if (item.kind === "event") {
      const segment = eventDaySegment(item.event, dateKey, timezone);
      return [{ kind: "event" as const, id: item.id, event: item.event, ...segment,
        startMinute: minute(segment.startAt), endMinute: Math.max(minute(segment.endAt), minute(segment.startAt) + SPOT_MINUTES) }];
    }
    return [];
  }));

  const open = params.openWindows.map((window) => ({ startMinute: minute(window.startAt), endMinute: minute(window.endAt) }))
    .filter((window) => window.endMinute > window.startMinute);

  const spots = params.gaps.flatMap((gap) => {
    const result: DayTimeline["spots"] = [];
    for (let time = gap.startAt.getTime(); time < gap.endAt.getTime(); time += SPOT_MINUTES * 60_000) {
      const startAt = new Date(time);
      const endAt = new Date(Math.min(time + SPOT_MINUTES * 60_000, gap.endAt.getTime()));
      const startMinute = minute(startAt);
      const endMinute = minute(endAt);
      if (endMinute > startMinute) result.push({ startAt, endAt, startMinute, endMinute });
    }
    return result;
  });

  const edges = [...open, ...blocks, ...spots];
  if (edges.length === 0) return { allDay: agenda.allDay, range: null, hours: [], open, closed: [], blocks, spots };

  const startMinute = Math.max(0, Math.floor((Math.min(...edges.map((edge) => edge.startMinute)) - CLOSED_MARGIN_MINUTES) / 30) * 30);
  const endMinute = Math.min(1440, Math.ceil((Math.max(...edges.map((edge) => edge.endMinute)) + CLOSED_MARGIN_MINUTES) / 30) * 30);
  const closed: DayTimeline["closed"] = [];
  let cursor = startMinute;
  for (const window of [...open].sort((a, b) => a.startMinute - b.startMinute)) {
    if (window.startMinute > cursor) closed.push({ startMinute: cursor, endMinute: window.startMinute });
    cursor = Math.max(cursor, window.endMinute);
  }
  if (cursor < endMinute) closed.push({ startMinute: cursor, endMinute });
  const hours = Array.from({ length: 25 }, (_, hour) => hour * 60).filter((value) => value > startMinute && value < endMinute);

  return { allDay: agenda.allDay, range: { startMinute, endMinute }, hours, open, closed, blocks, spots };
}
